import React, { useState, useRef } from 'react';
import classNames from 'classnames';
import { useScrollPastElement } from 'hooks/useScrollPastElement';
import { useMediaQuery } from '@mui/material';
import rightArrow from '../../../public/images/arrow-right.svg';
import Image from 'next/image';
import PopUpContactUs from 'components/ContactUs/PopUpContactUs';
import { HEADER_HEIGHT } from './Header';

interface PageAnchorsProps {
  links: {
    id: string;
    name: string;
    nameMobile: string;
    section: string;
  }[];
  title: string;
  visibleSection: string | null;
  isNeighborhoodPage?: boolean;
  scrollPastRef: React.RefObject<HTMLElement | null>;
  handleLinkClick: (
    e: React.MouseEvent<HTMLAnchorElement>,
    sectionId: string
  ) => void;
  isHeaderHidden: boolean;
}

const MobileDropdown = ({
  links,
  title,
  visibleSection,
  handleLinkClick,
  isScrolledPast,
}: Pick<
  PageAnchorsProps,
  'links' | 'title' | 'visibleSection' | 'handleLinkClick'
> & { isScrolledPast: boolean }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <div className="relative flex-1 flex items-center justify-between w-full md:h-[49px]">
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="flex items-center px-4 py-2 pl-0 font-sohneKraftig w-full focus:outline-none focus-visible:bg-beige-light transition-colors"
        aria-expanded={isDropdownOpen}
        aria-controls="section-dropdown"
        aria-label={`${title} navigation menu`}
      >
        <span className="truncate">{title}</span>
        <span
          className={`ml-2 transition-transform flex-shrink-0 ${
            isDropdownOpen ? 'rotate-90' : ''
          }`}
        >
          <Image
            src={rightArrow}
            alt="Toggle Page Anchors"
            height={12}
            width={12}
            className="w-5 h-5"
          />
        </span>
      </button>
      {isDropdownOpen && (
        <div
          id="section-dropdown"
          role="menu"
          className="absolute top-full left-0 bg-white border border-beige-dark shadow-lg z-50 max-w-[301px]"
        >
          {links.map((sectionRef, index) => (
            <a
              key={sectionRef.id || index}
              onClick={(e) => {
                handleLinkClick(e, sectionRef.id);
                setIsDropdownOpen(false);
              }}
              href={`#${sectionRef.id}`}
              role="menuitem"
              aria-current={
                visibleSection === sectionRef.section ? 'location' : undefined
              }
              className="block px-4 py-2 hover:bg-gray-100 border-b border-beige-dark last:border-b-0 focus:outline-none focus-visible:bg-beige-light transition-colors"
            >
              <span
                className={classNames('whitespace-nowrap pb-[2px]', {
                  'border-b-[2px] border-gold-dark':
                    visibleSection === sectionRef.section,
                })}
              >
                {sectionRef.name}
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

const DesktopNavigation = ({
  links,
  title,
  visibleSection,
  isNeighborhoodPage,
  isScrolledPast,
  handleLinkClick,
}: Omit<PageAnchorsProps, 'scrollPastRef'> & { isScrolledPast: boolean }) => {
  return (
    <>
      <div
        className={classNames(
          'font-sohneKraftig text-12-18-0.3 md:text-16-26-0.3 lg:text-16-26-0.3 pr-[20px] pl-0 min-w-0 flex-shrink xl:flex-col xl:justify-center xl:mb-[2px] ml-auto md:ml-0 mr-1 md:mr-0',
          'transition-all duration-500 ease-in-out overflow-hidden',
          {
            'max-w-0 opacity-0 pr-0': !isScrolledPast,
            'max-w-[300px] opacity-100': isScrolledPast,
          }
        )}
        aria-hidden={!isScrolledPast}
      >
        <div className="truncate transform transition-transform duration-500 ease-in-out">
          {title}
        </div>
      </div>
      <nav className="flex flex-row flex-nowrap" aria-label="Page sections">
        {links.map((sectionRef, index) => (
          <a
            onClick={(e) => handleLinkClick(e, sectionRef.id)}
            href={`#${sectionRef.id}`}
            key={sectionRef.id || index}
            aria-current={
              visibleSection === sectionRef.section ? 'location' : undefined
            }
            className={classNames(
              'mr-1 pr-1 md:pr-3 md:mr-3 lg:pr-3 lg:mr-3 border-r from-black cursor-pointer text-12-18-0.3 md:text-14-26-0.3 whitespace-pre-wrap border-gold-dark last:border-r-0',
              'focus:outline-none focus-visible:bg-beige-light transition-colors',
              {
                'hidden md:flex md:flex-wrap': isNeighborhoodPage,
              }
            )}
          >
            <div
              className={classNames('flex cursor-none whitespace-nowrap', {
                'selected border-b-[2px] border-gold-dark':
                  visibleSection === sectionRef.section,
              })}
            >
              <div className="hidden md:inline-block">{sectionRef.name}</div>
              <div className="md:hidden">{sectionRef.nameMobile}</div>
            </div>
          </a>
        ))}
      </nav>
    </>
  );
};

export const PageAnchors = (props: PageAnchorsProps) => {
  const { isScrolledPast } = useScrollPastElement(props.scrollPastRef);
  const isDesktop = useMediaQuery('(min-width: 1025px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');
  const isMobile = useMediaQuery('(max-width: 640px)');

  return (
    <div
      className={classNames(
        'flex flex-row items-center relative transition-[opacity,transform] duration-300 ease-in-out',
        {
          'container mx-auto': !isScrolledPast,
          'w-full px-0 gap-2': isTablet && isScrolledPast,
          container: !isTablet || !isScrolledPast,
          '!absolute': isMobile || isTablet,
          'bg-white': isMobile || isTablet,
          'md:h-[49px]': isScrolledPast || isDesktop,
        }
      )}
      style={{ height: isDesktop ? `${HEADER_HEIGHT.desktop}px` : undefined }}
    >
      {(isMobile || isTablet) && isScrolledPast ? (
        <div
          className={classNames('flex-1', {
            'max-w-[calc(100%-103px)]': !props.isHeaderHidden,
            'pl-[20px]': isScrolledPast && isMobile,
            'pl-[45px]': isScrolledPast && isTablet && !isMobile,
          })}
        >
          <MobileDropdown {...props} isScrolledPast={isScrolledPast} />
        </div>
      ) : isDesktop ? (
        <DesktopNavigation {...props} isScrolledPast={isScrolledPast} />
      ) : null}
      {isScrolledPast &&
        !props.isHeaderHidden &&
        (isDesktop ? (
          <div className="ml-auto flex-shrink-0">
            <PopUpContactUs
              gtmId="Contact_Us_Start_Header"
              flagContainer=" pb-[7px] pt-[7px] md:pt-[7px]"
              showName="inline-block"
              phoneNumber="inline-block"
              phoneIcon="hidden"
              flagPosition=" s:-left-5"
              formLocation="header_popup"
            />
          </div>
        ) : (
          <PopUpContactUs
            isMobile={isMobile}
            formLocation="header_popup"
            phoneIcon="!block s:pl-[14px]"
            showName="!hidden"
            phoneNumber="!hidden"
            flagContainer={classNames(
              '!pb-0.5 !pt-[3px] !pr-2 !h-[31px] s:!h-[33px]',
              {
                '!min-w-[95px]': !props.isHeaderHidden,
              }
            )}
          />
        ))}
    </div>
  );
};
